/**
 * Base client for EIP-712 signed API operations
 * Shared by FiClient and EcoClient: HTTP client, wallet init,
 * nonce management, signature builder, and hex-to-base64 signature conversion.
 *
 * Nonce lifecycle: The Morpheum system manages nonces — external clients do NOT
 * maintain local nonce counters. Before each transaction the client queries
 * GET /router/v1/nonce/status/{address}?reserve=true to atomically obtain the
 * next nonce, matching the E2E pattern (standards/tx/tx_builder.go InitializeNonceFromServer).
 */

import { HttpClient } from "../../transport/http/HttpClient";
import mormSigner from "../../signing/mormSigner";
import { NonceManager } from "../../utils/nonceManager";
import { InfoClient } from "../info/InfoClient";
import { BaseSignatureBuilder, NonceProvider } from "./BaseSignatureBuilder";
import { BaseSignedClientConfig } from "../../types/client";

export abstract class BaseSignedClient {
  protected httpClient: HttpClient;
  protected infoClient: InfoClient;
  protected nonceManager?: NonceManager;
  protected mnemonic?: string;
  protected chainID: number;
  protected walletAddress?: string;
  protected signatureBuilder?: BaseSignatureBuilder;

  constructor(config: BaseSignedClientConfig) {
    this.httpClient = new HttpClient(config.baseURL, config.maxRetries ?? 3);
    this.infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.sessionToken,
    });
    this.chainID = config.chainID ?? 10900;
    this.mnemonic = config.mnemonic;
    if (config.mnemonic) {
      this.nonceManager = new NonceManager(this.infoClient);
    }
  }

  /**
   * Get or create signature builder. Must be called after initialize() when walletAddress is set.
   */
  protected getSignatureBuilder(): BaseSignatureBuilder {
    if (!this.signatureBuilder) {
      if (!this.walletAddress) {
        throw new Error(
          this.getNotInitializedMessage(),
        );
      }
      if (!this.mnemonic) {
        throw new Error("Mnemonic is required for write operations");
      }
      this.signatureBuilder = new BaseSignatureBuilder({
        mnemonic: this.mnemonic,
        walletAddress: this.walletAddress,
        chainID: this.chainID,
      });
    }
    return this.signatureBuilder;
  }

  /**
   * Override in subclass to customize "not initialized" error message.
   */
  protected getNotInitializedMessage(): string {
    return "Client not initialized. Call initialize() first.";
  }

  /**
   * Get NonceManager. Throws if mnemonic was not provided (no NonceManager).
   */
  protected getNonceManager(): NonceManager {
    if (!this.nonceManager) {
      throw new Error("NonceManager not initialized. Mnemonic required.");
    }
    return this.nonceManager;
  }

  /**
   * Get nonce provider adapter for BaseSignatureBuilder.
   * Each call queries the server for the next nonce (system-managed).
   */
  protected getNonceProvider(): NonceProvider {
    return {
      getNextNonce: () => this.getNextNonce(),
    };
  }

  /**
   * Initialize wallet address from mnemonic. Required before signed operations.
   */
  async initialize(): Promise<void> {
    if (!this.mnemonic) {
      throw new Error("Mnemonic is required for write operations");
    }
    await mormSigner.initialize();
    const result = await mormSigner.generateAddress(this.mnemonic, 0);
    if (!result.success || !result.address) {
      throw new Error(`Failed to generate wallet address: ${result.error}`);
    }
    this.walletAddress = result.address;
    this.signatureBuilder = new BaseSignatureBuilder({
      mnemonic: this.mnemonic,
      walletAddress: this.walletAddress,
      chainID: this.chainID,
    });
  }

  /**
   * Get current wallet address. Throws if not initialized.
   */
  getWalletAddress(): string {
    if (!this.walletAddress) {
      throw new Error(this.getNotInitializedMessage());
    }
    return this.walletAddress;
  }

  /**
   * Refresh nonce from server (e.g. on nonce errors).
   * @deprecated Nonces are now queried per-transaction via getNextNonce(). Kept for backward compat.
   */
  async refreshNonceFromServer(): Promise<void> {
    const walletAddress = this.getWalletAddress();
    const response = await this.infoClient.getNonceStatus(walletAddress, false);
    console.log("[BaseSignedClient] Refreshed nonce from server", {
      walletAddress,
      serverNextNonce: response.nextNonce,
    });
  }

  /**
   * Get next nonce from the Morpheum system.
   *
   * Design: External clients do not manage nonces. Before each transaction the
   * client queries GET /router/v1/nonce/status/{address}?reserve=true to
   * atomically obtain and reserve the next nonce. This matches the Go E2E
   * pattern (standards/tx/tx_builder.go InitializeNonceFromServer + signer.GetNonceAndIncrement).
   *
   * The server returns { nextNonce } — the exact nonce to include in the signed payload.
   */
  protected async getNextNonce(): Promise<string> {
    const walletAddress = this.getWalletAddress();
    const response = await this.infoClient.getNonceStatus(walletAddress, true);
    const nextNonce = response.nextNonce;
    console.log("[BaseSignedClient] Server assigned nonce", {
      walletAddress,
      nextNonce,
    });
    return nextNonce.toString();
  }

  /**
   * Convert ECDSA signature from hex to base64 for TxRequest.signatures (ingress expects base64).
   */
  protected hexSignatureToBase64(signature: string): string {
    const raw = signature.replace(/^0x/i, "");
    if (/^[0-9a-fA-F]+$/.test(raw) && (raw.length === 128 || raw.length === 130)) {
      const len = raw.length / 2;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = parseInt(raw.slice(i * 2, i * 2 + 2), 16);
      }
      return btoa(String.fromCharCode(...bytes));
    }
    return signature;
  }
}
